import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import sortBy from 'lodash/sortBy';
import formMessages from '../form-messages';
import messages from './messages';
import FormControl from '@mui/material/FormControl';
import { isValidPhoneNumber, toNational } from '~/utils/phone-number-helpers';
import UserAutocompleteField from '../UserAutocompleteField';
import { useCongregations } from '~/hooks/api/useCongregations';
import FormLabel from '@mui/material/FormLabel';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/system/Stack';
import { useAppForm } from '../Form';

const createFormSchema = (intl: IntlShape) =>
  z.object({
    firstName: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) }),
    lastName: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) }),
    email: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .email({ message: intl.formatMessage(formMessages.invalidEmailAddress) }),
    phone: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) })
      .refine((val) => isValidPhoneNumber(val), {
        message: intl.formatMessage(messages.invalidPhoneNumber),
      }),
    congregation: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
    partner: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
      })
      .nullish(),
    gender: z.string(),
    experienceLevel: z.string(),
    privilege: z.string(),
    pioneerLevel: z.string(),
    languages: z
      .string()
      .array()
      .nonempty({ message: intl.formatMessage(formMessages.required) }),
    isVip: z.boolean(),
    isShiftLeader: z.boolean(),
    isAdministrator: z.boolean(),
    isPublisher: z.boolean(),
    middleName: z.string().nullish(),
    birthDate: z.string().nullish(),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const defaultValues: Omit<FormValues, 'congregation' | 'languages'> & {
  congregation?: string;
  languages: string[];
} = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  gender: 'Female',
  partner: null,
  experienceLevel: 'Common',
  privilege: 'None',
  pioneerLevel: 'None',
  languages: [],
  isVip: false,
  isShiftLeader: false,
  isAdministrator: false,
  isPublisher: true,
  congregation: undefined,
  phone: '',
  birthDate: null,
};

type UserFormProps = {
  initialValues?: typeof defaultValues;
  emailFixed?: boolean;
  onSubmit: (values: {
    firstName: string;
    middleName?: string | null;
    lastName: string;
    email: string;
    gender: string;
    partner: number | null;
    experienceLevel: string;
    privilege: string;
    pioneerLevel: string;
    languages: string[];
    isVip: boolean;
    isShiftLeader: boolean;
    isAdministrator: boolean;
    isPublisher: boolean;
    phone: string;
    congregation: number | null;
    birthDate?: string | null;
  }) => void;
};

const UserForm = (props: UserFormProps) => {
  const intl = useIntl();
  const initialPhone = isValidPhoneNumber(props.initialValues?.phone)
    ? toNational(props.initialValues?.phone)
    : props.initialValues?.phone;
  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
      ...(props.initialValues || {}),
      phone: initialPhone || '',
    },
    validators: {
      onChange: createFormSchema(intl),
    },
    onSubmit({ value }) {
      props.onSubmit({
        ...value,
        congregation: Number(value.congregation) || null,
        partner: value.partner?.id || null,
      });
    },
  });
  // Load all congregations (not just the first page of 20) so every one is
  // selectable. The AutocompleteField below lets the user type to filter, so a
  // large list stays usable. Switch to server-side search if a tenant ever has
  // thousands of congregations.
  const { data: { data: congregations = [] } = {}, isLoading } =
    useCongregations({ perPage: 1000 });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <form.AppField name="firstName">
          {(field) => (
            <field.TextField
              label={<FormattedMessage {...messages.firstName} />}
            />
          )}
        </form.AppField>
        <form.AppField name="middleName">
          {(field) => (
            <field.TextField
              label={<FormattedMessage {...messages.middleName} />}
            />
          )}
        </form.AppField>
        <form.AppField name="lastName">
          {(field) => (
            <field.TextField
              label={<FormattedMessage {...messages.lastName} />}
            />
          )}
        </form.AppField>
        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label={<FormattedMessage {...messages.email} />}
              disabled={props.emailFixed}
            />
          )}
        </form.AppField>
        <form.AppField name="gender">
          {(field) => (
            <field.SelectField
              label={<FormattedMessage {...messages.gender} />}
              options={[
                {
                  value: 'Male',
                  label: <FormattedMessage {...messages.male} />,
                },
                {
                  value: 'Female',
                  label: <FormattedMessage {...messages.female} />,
                },
              ]}
            />
          )}
        </form.AppField>
        <form.Field name="partner">
          {(field) => (
            <UserAutocompleteField
              label={<FormattedMessage {...messages.partner} />}
              input={{
                value: field.state.value || undefined,
                onChange: (partner) => field.handleChange(partner),
              }}
            />
          )}
        </form.Field>
        <form.AppField name="phone">
          {(field) => (
            <field.TextField label={<FormattedMessage {...messages.phone} />} />
          )}
        </form.AppField>
        <form.AppField name="birthDate">
          {(field) => (
            <field.DateField
              label={<FormattedMessage {...messages.birthDate} />}
            />
          )}
        </form.AppField>
        <form.AppField name="languages">
          {(field) => <field.LanguageField />}
        </form.AppField>
        {isLoading ? null : (
          <form.AppField name="congregation">
            {(field) => (
              <field.AutocompleteField
                label={<FormattedMessage {...messages.congregation} />}
                loading={isLoading}
                options={sortBy(congregations, 'name').map((cong) => ({
                  value: String(cong.id),
                  label: cong.name,
                }))}
              />
            )}
          </form.AppField>
        )}
        <form.AppField name="pioneerLevel">
          {(field) => (
            <field.RadioGroupField
              label={<FormattedMessage {...messages.pioneerLevel} />}
              options={[
                {
                  value: 'Regular',
                  label: <FormattedMessage {...messages.regularPioneer} />,
                },
                {
                  value: 'Special',
                  label: <FormattedMessage {...messages.sepcialPioneer} />,
                },
                {
                  value: 'CircuitOverseer',
                  label: <FormattedMessage {...messages.circuitOverseer} />,
                },
                {
                  value: 'None',
                  label: <FormattedMessage {...messages.noPioneer} />,
                },
              ]}
            />
          )}
        </form.AppField>
        <form.AppField name="privilege">
          {(field) => (
            <field.RadioGroupField
              label={<FormattedMessage {...messages.privilege} />}
              options={[
                {
                  value: 'Elder',
                  label: <FormattedMessage {...messages.elder} />,
                },
                {
                  value: 'MinisterialServant',
                  label: <FormattedMessage {...messages.ministerialServant} />,
                },
                {
                  value: 'None',
                  label: <FormattedMessage {...messages.none} />,
                },
              ]}
            />
          )}
        </form.AppField>
        <form.AppField name="experienceLevel">
          {(field) => (
            <field.RadioGroupField
              label={<FormattedMessage {...messages.experience} />}
              options={[
                {
                  value: 'Newbie',
                  label: <FormattedMessage {...messages.newbie} />,
                },
                {
                  value: 'Mentor',
                  label: <FormattedMessage {...messages.mentor} />,
                },
                {
                  value: 'Common',
                  label: <FormattedMessage {...messages.common} />,
                },
              ]}
            />
          )}
        </form.AppField>
        <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
          <FormLabel component="legend">
            <FormattedMessage {...messages.roles} />
          </FormLabel>
          <FormGroup>
            <form.AppField name="isAdministrator">
              {(field) => (
                <field.CheckboxField
                  label={<FormattedMessage {...messages.administrator} />}
                />
              )}
            </form.AppField>
            <form.AppField name="isVip">
              {(field) => (
                <field.CheckboxField
                  label={<FormattedMessage {...messages.vip} />}
                />
              )}
            </form.AppField>
            <form.AppField name="isPublisher">
              {(field) => (
                <field.CheckboxField
                  label={<FormattedMessage {...messages.publisher} />}
                />
              )}
            </form.AppField>
            <form.AppField name="isShiftLeader">
              {(field) => (
                <field.CheckboxField
                  label={<FormattedMessage {...messages.shiftLeader} />}
                />
              )}
            </form.AppField>
          </FormGroup>
        </FormControl>
        <div>
          <form.AppForm>
            <form.SubmitButton>
              <FormattedMessage {...formMessages.save} />
            </form.SubmitButton>
          </form.AppForm>
        </div>
      </Stack>
    </form>
  );
};

export default UserForm;
